// src/components/looker/CurrencyPicker.tsx
/**
 * FR-X-02: the page's numbers are never a sum across currencies. When the
 * scope holds spend in more than one currency the server forces one; this is
 * where the viewer picks which. With one currency (or none) nothing is forced
 * and the picker is not drawn.
 *
 * @module components/looker/CurrencyPicker
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { LOOKER_FIELD_CLASS, LOOKER_LABEL_CLASS } from './LookerFilterBar';

export interface CurrencyPickerProps {
  /** The scope's currencies with spend, most spend first (payload.currencies). */
  currencies: readonly string[];
  /** The currency the numbers on screen are in (payload.currency). */
  value: string | null;
  /** Accounts of unknown currency are left out by the forced currency. */
  unknownExcluded: boolean;
  onChange: (currency: string) => void;
  disabled?: boolean;
}

export const CurrencyPicker: React.FC<CurrencyPickerProps> = ({ currencies, value, unknownExcluded, onChange, disabled = false }) => {
  const { t } = useTranslation();
  if (currencies.length <= 1) return null;
  return (
    <div className="flex flex-col gap-1">
      <label className={LOOKER_LABEL_CLASS}>
        {t('looker.currency.label')}
        <select
          aria-label={t('looker.currency.label')}
          className={LOOKER_FIELD_CLASS}
          value={value ?? currencies[0]}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
        >
          {currencies.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </label>
      {unknownExcluded && <p className="text-xs text-neutral-700">{t('looker.currency.unknownExcluded')}</p>}
    </div>
  );
};
